import {
  CreateRestrictedUsernameDto,
  RestrictedUsernameDto,
} from "@shared/types/restricted-usernames/restricted-usernames.dto";
import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  Modal,
  notification,
  Space,
} from "antd";
import Papa from "papaparse";
import { useEffect, useRef, useState } from "react";
import { CSVUser } from "../RestrictedUsernames.types";

const requiredFieldRule = {
  required: true,
  whitespace: true,
  message: "This field is required",
};

interface IModal {
  open: boolean;
  restricedUsername: RestrictedUsernameDto | null;
  onClose: () => void;
  onSave: (data: RestrictedUsernameDto | CreateRestrictedUsernameDto) => void;
  isLoading: boolean;
  isError?: boolean;
  error?: string;
}

const ChangeRestrictedUsernameModal = ({
  open,
  restricedUsername,
  onClose,
  onSave,
  isLoading,
  isError,
  error,
}: IModal) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isEditingMode = !!restricedUsername?.id;

  const [form] = Form.useForm();
  const [_, setConverting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filteredUsernames, setFilteredUsernames] = useState<string[]>([]);

  useEffect(() => {
    if (restricedUsername) {
      form.setFieldsValue(restricedUsername);
    }
  }, [form, restricedUsername]);

  const clearInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileUpload = async (event: any) => {
    try {
      const usernames: string[] = [];

      setFilteredUsernames(usernames);

      let file = event.target.files?.[0] ?? null;

      clearInput();

      if (!file) {
        return;
      }

      setSelectedFile(file);
      setConverting(true);

      Papa.parse(file, {
        complete: (result: Papa.ParseResult<CSVUser>) => {
          const requiredColumns: (keyof CSVUser)[] = ["username"];
          const actualColumns = Object.keys(result.data[0] || {});

          if (
            !requiredColumns.every((column) => actualColumns.includes(column))
          ) {
            return notification.error({
              message: 'CSV file must contain "username" column.',
            });
          }

          const lastElement = result.data[result.data.length - 1];

          if (lastElement.username === "") {
            result.data.splice(result.data.length - 1, 1);
          }

          for (const { username } of result.data) {
            const trimmedNickname = username?.trim();
            const isValid =
              trimmedNickname && trimmedNickname.split(" ").length === 1;

            if (isValid) {
              usernames.push(trimmedNickname);
            } else {
              notification.info({
                message:
                  'Invalid CSV data. Please ensure all rows have valid "usernames" values.',
              });
              return;
            }
          }

          if (usernames.length === 0) {
            notification.error({
              message: "No valid data found in the CSV file.",
            });
          }

          setFilteredUsernames([...usernames]);

          form.setFields([
            {
              name: "username",
              errors: [],
            },
          ]);

          form.setFieldValue("username", "");
        },
        header: true,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setConverting(false);
    }
  };

  const handleFinish = (values: any) => {
    if (isEditingMode) {
      onSave(values);
    } else {
      onSave({
        usernames: selectedFile ? filteredUsernames : [values.username],
      });
    }
  };

  return (
    <Modal
      title={
        isEditingMode ? "Change Restricted Username" : "Add Restricted Username"
      }
      open={open}
      onCancel={onClose}
      footer={null}
    >
      <Form
        key={JSON.stringify(restricedUsername)}
        form={form}
        requiredMark={false}
        onFinish={handleFinish}
        layout="vertical"
      >
        {isError && (
          <Form.Item>
            <Alert
              message="Error"
              description={
                error ??
                "An error occurred while saving the restricted username data. Please try again."
              }
              type="error"
              showIcon
            />
          </Form.Item>
        )}

        <Form.Item
          name="username"
          label="Name"
          rules={!selectedFile ? [requiredFieldRule] : []}
        >
          <Input />
        </Form.Item>

        {isEditingMode === false ? (
          <>
            <Divider
              orientation="center"
              variant="solid"
              style={{
                paddingInline: 10,
                fontWeight: "bold",
                borderColor: "#d9d9d9",
                marginTop: 0,
                marginInline: 0,
                color: "#d9d9d9",
              }}
            >
              Or
            </Divider>

            <Form.Item label={"CSV File"}>
              <Input
                type="text"
                readOnly
                value={selectedFile ? selectedFile.name : "Choose file"}
                style={{ width: "100%", cursor: "pointer" }}
                onClick={() => {
                  fileInputRef.current?.click();
                }}
              />

              <input
                style={{
                  display: "none",
                }}
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
              />
            </Form.Item>
          </>
        ) : null}

        <Space
          style={{ justifyContent: "flex-end", width: "100%", marginTop: 15 }}
        >
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {isEditingMode
              ? "Save"
              : `Add${
                  filteredUsernames.length
                    ? ` (${filteredUsernames.length})`
                    : ""
                }`}
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};

export default ChangeRestrictedUsernameModal;
